import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { Fragment, useMemo, useState } from "react";
import AddUserDialog from "components/dialogs/Users/AddUserDialog";
import UpdateUserDialog from "components/dialogs/Users/UpdateUserDialog";
import { Alert, Snackbar, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Cell, Column, HeaderGroup, Row, useExpanded, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import { TFunction } from "i18next";
import { GlobalFilter, renderFilterTypes } from "utils/react-table";
import { LinkOutlined, DeleteTwoTone, EditTwoTone, PlusOutlined } from "@ant-design/icons";
import { EmptyTable, HeaderSort, TablePagination } from "components/third-party/ReactTable";
import MainCard from "components/MainCard";
import ScrollX from "components/ScrollX";
import { getUserRoleName } from "helpers/UserHelper";
import React from "react";
import GenericDeleteDialog from "components/dialogs/Generic/GenericDeleteDialog";
import { useAppSelector } from "app/hooks";
import { useGetUsersQuery, usePostUserMutation, usePutUserMutation, useDeleteUserMutation } from "features/api/apiSlice";
import { selectCurrentUser } from "features/auth/authSlice";
import { IUser, UserProviderId, UserRole } from "models/IUser";
import { useTranslation } from "react-i18next";
import moment from "moment";
import i18n from "app/i18n";
import { getDeviceBrandName } from "helpers/ExternalDeviceHelper";

// ==============================|| USERS PAGE ||============================== //

export default function UsersPage() {
  const { t } = useTranslation();
  const theme = useTheme();

  const authenticatedUser = useAppSelector(selectCurrentUser)
  const { data: childrenUsers, isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useGetUsersQuery({ parentId: authenticatedUser!!.id!!, role: null })
  const allUsers = useMemo(() => childrenUsers?.concat(authenticatedUser!!), [childrenUsers, authenticatedUser])

  const [user, setUser] = useState<any>(null);
  const [showAddUserDialog, setShowAddUserDialog] = useState<boolean>(false);
  const [showUpdateUserDialog, setShowUpdateUserDialog] = useState<boolean>(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState<boolean>(false);

  const handleOpenAddUserDialog = () => {
    setShowAddUserDialog(true)
  };

  const handleCloseAddUserDialog = () => {
    setShowAddUserDialog(false);
  };

  const handleOpenUpdateUserDialog = (user: IUser) => {
    setUser(user)
    setShowUpdateUserDialog(true)
  };

  const handleCloseUpdateUserDialog = () => {
    setShowUpdateUserDialog(false);
    setUser(null);
  };

  const handleOpenDeleteUserDialog = (user: IUser) => {
    setUser(user)
    setShowDeleteUserDialog(true)
  };

  const handleCloseDeleteUserDialog = () => {
    setShowDeleteUserDialog(false);
    setUser(null);
  };

  const [postUser] = usePostUserMutation();
  const handleAddUser = async (firstName: string, lastName: string, email: string, password: string, parentId: string, role: UserRole, culture: string | null, enrolmentDate: string | null, provider: string | null, meta: string | null) => {
    await postUser({firstName, lastName, email, password, parentId, role, culture, enrolmentDate, provider, meta})
      .unwrap()
      .then((payload) => {
        setShowAddUserDialog(false);
      })
      .catch((error) => {
        setSnackbarSeverity('error');
        setSnackbarMessage(t(error.data.literalKey as string, error.data.message as string))
        setShowSnackbar(true)
      })
  };

  const [putUser] = usePutUserMutation();
  const handleUpdateUser = async (userId: string, firstName: string, lastName: string, email: string, culture: string | null, parentId: string, enrolmentDate: string | null, meta: string | null) => {
    await putUser({userId, putUser: {firstName, lastName, email, culture, parentId, enrolmentDate, meta}})
      .unwrap()
      .then((payload) => {
        setShowUpdateUserDialog(false);
      })
      .catch((error) => {
        setSnackbarSeverity('error');
        setSnackbarMessage(t(error.data.literalKey as string, error.data.message as string))
        setShowSnackbar(true)
      })
  };

  const [deleteUser] = useDeleteUserMutation();
  const handleDeleteUser = async (userId: string) => {
      await deleteUser(userId)
        .unwrap()
        .then((payload) => {
          setShowDeleteUserDialog(false);
        })
        .catch((error) => {
          setSnackbarSeverity('error');
          setSnackbarMessage(t(error.data.literalKey as string, error.data.message as string))
          setShowSnackbar(true)
        })
  };

  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>();
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('error');

  const handleCopyPairingLink = async (owUserId: string) => {
    const pairingLink = `https://ow.excare.io/users/${owUserId}/pair`;

    try {
      await navigator.clipboard.writeText(pairingLink);
      setSnackbarSeverity('success');
      setSnackbarMessage(t('pairing_link_copied', 'Pairing link copied'));
      setShowSnackbar(true);
    } catch {
      setSnackbarSeverity('error');
      setSnackbarMessage(t('copy_pairing_link_error', 'Could not copy pairing link'));
      setShowSnackbar(true);
    }
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowSnackbar(false);
    setSnackbarMessage(null)
  };

  const isEXCAREAdmin = authenticatedUser?.role === UserRole.Admin && authenticatedUser?.provider === UserProviderId.EXCARE;

  const columns = useMemo(
    () => [
      {
        Header: t('id', 'Id'),
        accessor: 'id'
      },
      {
        Header: t('name', 'Name'),
        accessor: 'firstName',
        sortType: (rowA: Row, rowB: Row) => {
          const userA = rowA.original as IUser;
          const userB = rowB.original as IUser;

          const fullNameA = `${userA.firstName ?? ''} ${userA.lastName ?? ''}`.trim();
          const fullNameB = `${userB.firstName ?? ''} ${userB.lastName ?? ''}`.trim();
          const nameCompare = fullNameA.localeCompare(fullNameB, i18n.language, { sensitivity: 'base' });

          if (nameCompare !== 0) {
            return nameCompare;
          }

          return String(userA.id ?? '').localeCompare(String(userB.id ?? ''), i18n.language, { sensitivity: 'base' });
        },
        Cell: ({ row }: { row: Row }) => {
          const { values } = row;
          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack spacing={0}>
                <Typography>{values.firstName} {values.lastName}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {values.id}
                </Typography>
              </Stack>
            </Stack>
          );
        }
      },
      {
        Header: t('last_name', 'Last name'),
        accessor: 'lastName'
      },
      {
        Header: t('email', 'Email'),
        accessor: 'email'
      },
      ...(isEXCAREAdmin
        ? [{ Header: t('provider', 'Provider'), accessor: 'provider' }]
        : []),
      {
        Header: t('role', 'Role'),
        accessor: 'role',
        sortType: (rowA: Row, rowB: Row) => {
          const roleA = (rowA.original as IUser).role;
          const roleB = (rowB.original as IUser).role;
          const labelA = getUserRoleName(roleA as UserRole);
          const labelB = getUserRoleName(roleB as UserRole);

          return labelA.localeCompare(labelB, i18n.language, { sensitivity: 'base' });
        },
        Cell: ({ value }: { value: UserRole }) => {
          switch (value) {
            case UserRole.Admin:
              return <Typography>{getUserRoleName(UserRole.Admin)}</Typography>;
            case UserRole.Manager:
              return <Typography>{getUserRoleName(UserRole.Manager)}</Typography>;
            case UserRole.Clinician:
              return <Typography>{getUserRoleName(UserRole.Clinician)}</Typography>;
            case UserRole.Patient:
              return <Typography>{getUserRoleName(UserRole.Patient)}</Typography>;
            default:
              return <Typography>{t('unknown', 'Unknown')}</Typography>;
          }
        }
      },
      ...(isEXCAREAdmin
        ? [{ Header: t('open_wearables_user_id', 'OpenWearables User Id'), accessor: 'owUserId' }]
        : []),
      {
        Header: t('parent', 'Parent'),
        accessor: 'parentId',
        sortType: (rowA: Row, rowB: Row) => {
          const parentA = allUsers?.find(x => x.id === (rowA.original as IUser).parentId);
          const parentB = allUsers?.find(x => x.id === (rowB.original as IUser).parentId);

          const fullNameA = `${parentA?.firstName ?? ''} ${parentA?.lastName ?? ''}`.trim();
          const fullNameB = `${parentB?.firstName ?? ''} ${parentB?.lastName ?? ''}`.trim();
          const nameCompare = fullNameA.localeCompare(fullNameB, i18n.language, { sensitivity: 'base' });

          if (nameCompare !== 0) {
            return nameCompare;
          }

          return String(parentA?.id ?? '').localeCompare(String(parentB?.id ?? ''), i18n.language, { sensitivity: 'base' });
        },
        Cell: ({ row }: { row: Row }) => {
          const parent = allUsers?.find(x => x.id == row.values.parentId)

          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack spacing={0}>
                <Typography>{parent?.firstName} {parent?.lastName}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {parent?.id}
                </Typography>
              </Stack>
            </Stack>
          );
        }
      },
      {
        Header: t('enrolment', 'Enrolment'),
        accessor: 'enrolmentAt',
        sortType: (rowA: Row, rowB: Row) => {
          const a = (rowA.original as IUser).enrolmentAt;
          const b = (rowB.original as IUser).enrolmentAt;
          return (a ? new Date(a).getTime() : 0) - (b ? new Date(b).getTime() : 0);
        },
        Cell: ({ row }: { row: Row }) => {
          const item = row.original as IUser;
          return (
            <Typography>{item.enrolmentAt ? moment(item.enrolmentAt.toString()).locale(i18n.language).format('L') : ''}</Typography>
          );
        }
      },
      {
        Header: t('last_synced', 'Last synced'),
        accessor: 'lastSyncedAt',
        sortType: (rowA: Row, rowB: Row) => {
          const a = (rowA.original as IUser).lastSyncedAt;
          const b = (rowB.original as IUser).lastSyncedAt;
          return (b ? new Date(b).getTime() : 0) - (a ? new Date(a).getTime() : 0);
        },
        Cell: ({ row }: { row: Row }) => {
          const item = row.original as IUser;

          return (
            <Stack spacing={0.5} alignItems="flex-start">
              <Typography>{item.lastSyncedAt ? moment(item.lastSyncedAt.toString()).locale(i18n.language).fromNow() : ''}</Typography>
              {item.lastSyncedFrom && (
                <Badge
                  badgeContent={getDeviceBrandName(item.lastSyncedFrom)}
                  color="default"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    '& .MuiBadge-badge': {
                      position: 'static',
                      transform: 'translateY(-1px)',
                      border: '1.5px solid',
                      borderColor: 'text.secondary',
                      bgcolor: 'transparent',
                      px: 0.75,
                      py: 0,
                      fontSize: '11px',
                      fontWeight: 700,
                      lineHeight: 'normal'
                    }
                  }}
                >
                  <span />
                </Badge>
              )}
            </Stack>
          );
        }
      },
      {
        Header: t('last_value', 'Last value'),
        accessor: 'lastValueAt',
        sortType: (rowA: Row, rowB: Row) => {
          const a = (rowA.original as IUser).lastValueAt;
          const b = (rowB.original as IUser).lastValueAt;
          return (b ? new Date(b).getTime() : 0) - (a ? new Date(a).getTime() : 0);
        },
        Cell: ({ row }: { row: Row }) => {
          const item = row.original as IUser;
          return (
            <Typography>{item.lastValueAt ? moment(item.lastValueAt.toString()).locale(i18n.language).fromNow() : ''}</Typography>
          );
        }
      },
      {
        Header: t('actions', 'Actions'),
        className: 'cell-center',
        disableSortBy: true,
        Cell: ({ row }: { row: Row<{}> }) => {
          const item = row.original as IUser;

          return (
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
              {item.owUserId && (
                <Tooltip title={t('copy_pairing_link', 'Copy pairing link')}>
                  <IconButton
                    color="secondary"
                    onClick={() => handleCopyPairingLink(item.owUserId as string)}
                  >
                    <LinkOutlined />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('edit', 'Edit')}>
                <IconButton
                  color="primary"
                  onClick={() => handleOpenUpdateUserDialog(row.original)}
                >
                  <EditTwoTone twoToneColor={theme.palette.primary.main} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('delete', 'Delete')}>
                <IconButton
                  color="error"
                  onClick={() => handleOpenDeleteUserDialog(row.original)}
                >
                  <DeleteTwoTone twoToneColor={theme.palette.error.main} />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, allUsers, t, isEXCAREAdmin]
  );

  return (
    <>
      <MainCard content={false}>
        <ScrollX>
          <UsersTable
            columns={columns}
            users={childrenUsers === undefined ? [] : childrenUsers}
            handleOpenAddUserDialog={handleOpenAddUserDialog}
            getHeaderProps={(column: HeaderGroup) => column.getSortByToggleProps()}
            t={t}
            isLoading={isLoadingUsers}
            isFetching={isFetchingUsers}
          />
        </ScrollX>
        {showDeleteUserDialog &&
            <GenericDeleteDialog
                openDialog={showDeleteUserDialog}
                handleClose={handleCloseDeleteUserDialog}
                title={t('do_you_want_to_delete_x', 'Do you want to delete {0}?').replace('{0}', user?.id)}
                message={t('this_action_cannot_be_undone', 'This action cannot be undone.')}
                handleDelete={() => handleDeleteUser(user?.id)}
            />
        }
        
        {showAddUserDialog && <AddUserDialog users={allUsers!!} openDialog={showAddUserDialog} authenticatedUser={authenticatedUser!!} handleClose={handleCloseAddUserDialog} handleAddUser={handleAddUser} />}
        
        {showUpdateUserDialog && user && <UpdateUserDialog openDialog={showUpdateUserDialog} user={user} users={allUsers!!} handleClose={handleCloseUpdateUserDialog} handleUpdateUser={handleUpdateUser}  />}
        
        <Snackbar open={showSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center'}}>
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>{snackbarMessage}</Alert>
        </Snackbar>
      </MainCard>
    </>
  )
}

// ==============================|| REACT TABLE ||============================== //

interface Props {
    columns: Column[]
    users: IUser[]
    handleOpenAddUserDialog: () => void
    getHeaderProps: (column: HeaderGroup) => {}
    t: TFunction<"translation", undefined, "translation">
    isLoading: boolean
    isFetching: boolean
}

function UsersTable({ columns, users, handleOpenAddUserDialog, getHeaderProps, t, isLoading, isFetching }: Props) {
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));

    const filterTypes = useMemo(() => renderFilterTypes, []);
    const sortTypes = useMemo(() => ({
      alphanumeric: (rowA: Row, rowB: Row, columnId: string) => {
        const a = String(rowA.values[columnId] ?? '');
        const b = String(rowB.values[columnId] ?? '');
        return a.localeCompare(b, i18n.language, { sensitivity: 'base' });
      }
    }), []);
    const sortBy = { id: 'firstName', desc: false };

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
        page,
        gotoPage,
        setPageSize,
        state: { globalFilter, pageIndex, pageSize },
        preGlobalFilteredRows,
        setGlobalFilter
    } = useTable(
        {
        columns,
        data: users,
        filterTypes,
        sortTypes,
        disableSortRemove: true,
        initialState: { pageIndex: 0, pageSize: 10, hiddenColumns: ['id', 'lastName', 'email'], sortBy: [sortBy] }
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect
    );

  const visibleColumnsCount = headerGroups[0]?.headers?.length ?? 1;

  return (
    <Stack spacing={3}>
      <Stack
        direction={matchDownSM ? 'column' : 'row'}
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: 3, pb: 0 }}
        >
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          size="small"
        />
        <Stack direction={matchDownSM ? 'column' : 'row'} alignItems="center" spacing={1}>
          <Button variant="contained" startIcon={<PlusOutlined />} onClick={handleOpenAddUserDialog} size="small" sx={{textTransform: 'none'}}>
            {t('add_user', 'Add user')}
          </Button>
        </Stack>
      </Stack>
      <Table {...getTableProps()}>
      <TableHead>
        {headerGroups.map((headerGroup: HeaderGroup<{}>) => (
        <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column: HeaderGroup) => (
            <TableCell {...column.getHeaderProps([{ className: column.className }, getHeaderProps(column)])}>
                <HeaderSort column={column} />
            </TableCell>
            ))}
        </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()}>
        {
          isLoading ? (
            <EmptyTable msg={t('loading', 'Loading...')} colSpan={visibleColumnsCount} />
          ) : isFetching ? (
            <EmptyTable msg={t('fetching', 'Fetching...')} colSpan={visibleColumnsCount} />
          ) : page.length > 0 ? (
            page.map((row: Row, i: number) => {
              prepareRow(row);
    
              return (
                <Fragment key={i}>
                <TableRow
                  {...row.getRowProps()}
                  sx={{ cursor: 'pointer', bgcolor: 'inherit' }}
                  >
                    {row.cells.map((cell: Cell) => (
                    <TableCell {...cell.getCellProps([{ className: cell.column.className }])}>{cell.render('Cell')}</TableCell>
                    ))}
                </TableRow>
                {row.isExpanded}
                </Fragment>
              );
            })
          ) : (
            <EmptyTable msg={t('no_data_found', 'Data not found')} colSpan={visibleColumnsCount} />
          )
        }
        <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
        <TableCell sx={{ p: 2, py: 3 }} colSpan={visibleColumnsCount}>
            <TablePagination gotoPage={gotoPage} rows={rows} setPageSize={setPageSize} pageSize={pageSize} pageIndex={pageIndex} />
        </TableCell>
        </TableRow>
      </TableBody>
      </Table>
    </Stack>
  )
}